import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../api/client';
import toast from 'react-hot-toast';

function StatCard({ title, value, subtitle, accent = 'slate' }) {
  const colors = {
    slate: 'border-slate-700',
    indigo: 'border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-transparent',
    emerald: 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent',
    amber: 'border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent',
    purple: 'border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent',
    rose: 'border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-transparent',
  };
  return (
    <div className={`bg-slate-800 rounded-xl p-5 border ${colors[accent]}`}>
      <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">{title}</p>
      <p className="text-3xl font-bold text-white mt-2">{value ?? '—'}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

const SIGN_EMOJIS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
  Koç: '♈', Boğa: '♉', İkizler: '♊', Yengeç: '♋', Aslan: '♌', Başak: '♍',
  Terazi: '♎', Akrep: '♏', Yay: '♐', Oğlak: '♑', Kova: '♒', Balık: '♓',
};

const LANG_LABELS = { tr: '🇹🇷 Türkçe', en: '🇬🇧 English', de: '🇩🇪 Deutsch' };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const response = await client.get('/api/v1/admin/users/stats/overview');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Failed to load dashboard data</p>
          <button onClick={() => { setLoading(true); fetchStats(); }}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const signDist = stats.sign_distribution || {};
  const langDist = stats.language_distribution || {};
  const subDist = stats.subscription_distribution || {};
  const maxSign = Math.max(...Object.values(signDist), 1);

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">📊 Overview</h2>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Toplam Kullanıcı" value={stats.total_users} accent="indigo" />
        <StatCard title="Premium Kullanıcı" value={stats.premium_users} accent="amber" />
        <StatCard title="Misafir Kullanıcı" value={stats.guest_users} accent="slate" />
        <StatCard
          title="Bugün Yeni"
          value={stats.new_users?.today}
          subtitle={`Bu hafta: ${stats.new_users?.week ?? 0}`}
          accent="emerald"
        />
        <StatCard title="Bu Ay Yeni" value={stats.new_users?.month} accent="purple" />
        <StatCard title="AI Kullanımı (toplam)" value={stats.ai_usage} accent="rose" />
      </div>

      {/* Distribution Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Sign Distribution */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Burç Dağılımı</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(signDist)
              .sort(([, a], [, b]) => b - a)
              .map(([sign, count]) => {
                const pct = Math.round((count / maxSign) * 100);
                return (
                  <div key={sign} className="flex items-center gap-2 bg-slate-700/40 rounded-lg px-3 py-2">
                    <span className="text-lg">{SIGN_EMOJIS[sign] || '⭐'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-slate-300 truncate">{sign}</span>
                        <span className="text-white font-bold">{count}</span>
                      </div>
                      <div className="w-full bg-slate-600 rounded-full h-1.5">
                        <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Language + Subscription */}
        <div className="space-y-6">
          {/* Language Distribution */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Dil Dağılımı</h3>
            <div className="space-y-2">
              {Object.entries(langDist)
                .sort(([, a], [, b]) => b - a)
                .map(([lang, count]) => {
                  const total = Object.values(langDist).reduce((a, b) => a + b, 0);
                  const pct = total ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={lang} className="flex items-center justify-between">
                      <span className="text-slate-300 text-sm">{LANG_LABELS[lang] || lang}</span>
                      <span className="text-white font-bold text-sm">{count} <span className="text-slate-500 font-normal">({pct}%)</span></span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Subscription Distribution */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Abonelik</h3>
            <div className="space-y-2">
              {Object.entries(subDist).map(([tier, count]) => {
                const total = Object.values(subDist).reduce((a, b) => a + b, 0);
                const pct = total ? Math.round((count / total) * 100) : 0;
                const colors = { free: 'bg-slate-500', premium: 'bg-indigo-500', premium_plus: 'bg-amber-500' };
                return (
                  <div key={tier}>
                    <div className="flex justify-between text-sm mb-0.5">
                      <span className="text-slate-300 capitalize">{tier.replace('_', ' ')}</span>
                      <span className="text-white font-medium">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div className={`${colors[tier] || 'bg-indigo-500'} h-1.5 rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Hızlı Erişim</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/users" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm">
            👥 Kullanıcılar
          </Link>
          <Link to="/revenue" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm">
            💰 Gelir
          </Link>
          <Link to="/content" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm">
            📅 İçerik Üret
          </Link>
          <Link to="/caches" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm">
            🗃️ Cache
          </Link>
          <Link to="/error-logs" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm">
            ⚠️ Hata Logları
          </Link>
        </div>
      </div>
    </div>
  );
}
